import React, { useMemo, useState, createElement } from 'react';
import { motion } from 'framer-motion';
import { FileTextIcon, PlusIcon, DownloadIcon, EditIcon, Trash2Icon, SearchIcon } from 'lucide-react';
import { Task } from '../components/TaskCard';
import { Customer } from '../components/CustomerCard';
import { Button } from '../components/Button';
import { AddReportModal, ReportData } from '../components/AddReportModal';
interface ReportProps {
  tasks: Task[];
  customers: Customer[];
  isReadOnly?: boolean;
  reports: ReportData[];
  onAddReport: (report: Omit<ReportData, 'id' | 'createdAt'>) => void;
  onUpdateReport: (report: ReportData) => void;
  onDeleteReport: (reportId: string) => void;
}
export function Report({
  tasks,
  customers,
  isReadOnly = false,
  reports,
  onAddReport,
  onUpdateReport,
  onDeleteReport
}: ReportProps) {
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);
  const [editingReport, setEditingReport] = useState<ReportData | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  // Filter reports based on search query
  const filteredReports = useMemo(() => {
    if (!searchQuery.trim()) return reports;
    const query = searchQuery.toLowerCase();
    return reports.filter(report => {
      return report.title.toLowerCase().includes(query) || report.reportType.toLowerCase().includes(query) || report.meterNumber?.toLowerCase().includes(query) || report.installationNumber?.toLowerCase().includes(query) || new Date(report.date).toLocaleDateString('da-DK').includes(query);
    });
  }, [reports, searchQuery]);
  const handleAddOrUpdateReport = (reportData: Omit<ReportData, 'id' | 'createdAt'>) => {
    if (isReadOnly) return;
    if (editingReport) {
      // Update existing report
      const updatedReport: ReportData = {
        ...reportData,
        id: editingReport.id,
        createdAt: editingReport.createdAt
      };
      onUpdateReport(updatedReport);
      setEditingReport(null);
    } else {
      // Create new report
      onAddReport(reportData);
    }
  };
  const handleEditReport = (report: ReportData) => {
    if (isReadOnly) return;
    setEditingReport(report);
    setIsReportModalOpen(true);
  };
  const handleDeleteReport = (reportId: string) => {
    if (isReadOnly) return;
    if (window.confirm('Er du sikker på at du vil slette denne rapport?')) {
      onDeleteReport(reportId);
    }
  };
  const handleDownloadReport = (report: ReportData) => {
    // Create a formatted text version of the report
    let reportText = `RAPPORT: ${report.title}\n`;
    reportText += `${'='.repeat(50)}\n\n`;
    reportText += `Opgavetype: ${report.reportType}\n`;
    reportText += `Dato: ${new Date(report.date).toLocaleDateString('da-DK')}\n`;
    if (report.meterNumber) reportText += `Målernummer: ${report.meterNumber}\n`;
    if (report.installationNumber) reportText += `Installationsnummer: ${report.installationNumber}\n`;
    reportText += `\nGENERELLE KONTROLLER\n${'-'.repeat(50)}\n`;
    reportText += `Rengjort og ryddet op: ${report.cleanedUp === 'yes' ? 'Ja' : 'Nej'}\n`;
    reportText += `Ejer/bruger informeret: ${report.ownerInformed === 'yes' ? 'Ja' : report.ownerInformed === 'no' ? 'Nej' : 'Ikke relevant'}\n`;
    reportText += `Jordlederforbindelser tilgængelige: ${report.earthConnectionAccessibility === 'yes' ? 'Ja' : 'Nej'}\n`;
    reportText += `Barrierer eller kapslinger: ${report.barriersOrEnclosures === 'yes' ? 'Ja' : report.barriersOrEnclosures === 'no' ? 'Nej' : 'Ikke relevant'}\n`;
    reportText += `Tilstrækkelig arbejdsplads: ${report.sufficientWorkingSpace === 'yes' ? 'Ja' : 'Nej'}\n`;
    reportText += `\nKABELINSTALLATION\n${'-'.repeat(50)}\n`;
    reportText += `Kabler korrekt oplagt: ${report.cablesCorrectlyLaid === 'yes' ? 'Ja' : report.cablesCorrectlyLaid === 'no' ? 'Nej' : 'Ikke relevant'}\n`;
    reportText += `Kabler korrekt afsluttet: ${report.cablesCorrectlyTerminated === 'yes' ? 'Ja' : report.cablesCorrectlyTerminated === 'no' ? 'Nej' : 'Ikke relevant'}\n`;
    reportText += `Mekanisk beskyttelse: ${report.mechanicalProtection === 'yes' ? 'Ja' : report.mechanicalProtection === 'no' ? 'Nej' : 'Ikke relevant'}\n`;
    reportText += `\nMÆRKNING OG MATERIALER\n${'-'.repeat(50)}\n`;
    reportText += `Mærket med tilhørsforhold: ${report.circuitsLabeledWithAffiliation === 'yes' ? 'Ja' : report.circuitsLabeledWithAffiliation === 'no' ? 'Nej' : 'Ikke relevant'}\n`;
    reportText += `Mærket med max sikring: ${report.circuitsLabeledWithMaxFuse === 'yes' ? 'Ja' : report.circuitsLabeledWithMaxFuse === 'no' ? 'Nej' : 'Ikke relevant'}\n`;
    reportText += `Materialer installeret korrekt: ${report.materialsInstalledPerInstructions === 'yes' ? 'Ja' : 'Nej'}\n`;
    reportText += `\nMÅLINGER\n${'-'.repeat(50)}\n`;
    if (report.protectionConductorContinuity) reportText += `Kontinuitet: ${report.protectionConductorContinuity}\n`;
    if (report.insulationMeasurement) reportText += `Isolationsmåling: ${report.insulationMeasurement}\n`;
    if (report.voltageControl) reportText += `Spændingskontrol: ${report.voltageControl}\n`;
    if (report.rcdTripTime) reportText += `Fejlstrømsafbryder udkoblingstid: ${report.rcdTripTime}\n`;
    if (report.earthElectrodeResistance) reportText += `Overgangsmodstand til jordelektrode: ${report.earthElectrodeResistance}\n`;
    if (report.notes) {
      reportText += `\nYDERLIGERE NOTER\n${'-'.repeat(50)}\n${report.notes}\n`;
    }
    reportText += `\nOprettet: ${new Date(report.createdAt).toLocaleString('da-DK')}\n`;
    // Create and download the file
    const blob = new Blob([reportText], {
      type: 'text/plain;charset=utf-8'
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${report.title.replace(/[^a-z0-9]/gi, '_')}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };
  return <div className="space-y-6" data-id="element-1720">
      <header className="flex items-center justify-between" data-id="element-1721">
        <div data-id="element-1722">
          <h1 className="text-4xl font-bold text-stone-900 mb-2" data-id="element-1723">Rapport</h1>
          <p className="text-stone-600" data-id="element-1724">
            {isReadOnly ? 'Se dine rapporter' : 'Administrer og download dine rapporter'}
          </p>
        </div>
        {!isReadOnly && <Button onClick={() => {
        setEditingReport(null);
        setIsReportModalOpen(true);
      }} className="flex items-center gap-2" data-id="element-1725">
            <PlusIcon className="w-5 h-5" data-id="element-1726" />
            Tilføj Rapport
          </Button>}
      </header>

      {/* Search Bar */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} className="bg-white rounded-2xl p-6 border border-stone-200 shadow-sm" data-id="element-1727">
        <div className="relative" data-id="element-1728">
          <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" data-id="element-1729" />
          <input type="text" placeholder="Søg efter rapporter (titel, type, målernummer, dato...)" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-12 pr-4 py-3 rounded-lg border border-stone-200 bg-white text-stone-900 placeholder-stone-400 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all" data-id="element-1730" />
        </div>
      </motion.div>

      {/* Saved Reports Section */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.1
    }} className="bg-white rounded-2xl p-6 border border-stone-200 shadow-sm" data-id="element-1731">
        <div className="flex items-center justify-between mb-6" data-id="element-1732">
          <h2 className="text-lg font-semibold text-stone-900" data-id="element-1733">
            Gemte rapporter
            {searchQuery && <span className="ml-2 text-sm font-normal text-stone-500" data-id="element-1734">
                ({filteredReports.length}{' '}
                {filteredReports.length === 1 ? 'resultat' : 'resultater'})
              </span>}
          </h2>
          {reports.length > 0 && <p className="text-sm text-stone-500" data-id="element-1735">
              {reports.length} {reports.length === 1 ? 'rapport' : 'rapporter'}{' '}
              i alt
            </p>}
        </div>

        {filteredReports.length > 0 ? <div className="space-y-3" data-id="element-1736">
            {filteredReports.map((report, index) => <motion.div key={report.id} initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: index * 0.05
        }} className="flex items-center justify-between p-4 bg-stone-50 rounded-xl border border-stone-200 hover:border-teal-200 transition-colors" data-id="element-1737">
                <div className="flex-1" data-id="element-1738">
                  <div className="flex items-center gap-2 mb-1" data-id="element-1739">
                    <FileTextIcon className="w-5 h-5 text-teal-600" data-id="element-1740" />
                    <h3 className="font-semibold text-stone-900" data-id="element-1741">
                      {report.title}
                    </h3>
                    <span className="text-xs font-medium text-stone-500 bg-white px-2 py-0.5 rounded-full border border-stone-200" data-id="element-1742">
                      {report.reportType}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-stone-600" data-id="element-1743">
                    <span data-id="element-1744">
                      {new Date(report.date).toLocaleDateString('da-DK')}
                    </span>
                    {report.meterNumber && <>
                        <span className="text-stone-300" data-id="element-1745">•</span>
                        <span data-id="element-1746">Måler: {report.meterNumber}</span>
                      </>}
                    {report.installationNumber && <>
                        <span className="text-stone-300" data-id="element-1747">•</span>
                        <span data-id="element-1748">Installation: {report.installationNumber}</span>
                      </>}
                  </div>
                  <p className="text-xs text-stone-400 mt-1" data-id="element-1749">
                    Oprettet{' '}
                    {new Date(report.createdAt).toLocaleDateString('da-DK')}
                  </p>
                </div>
                <div className="flex items-center gap-2" data-id="element-1750">
                  <button onClick={() => handleDownloadReport(report)} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors" title="Download rapport" data-id="element-1751">
                    <DownloadIcon className="w-5 h-5" data-id="element-1752" />
                  </button>
                  {!isReadOnly && <>
                      <button onClick={() => handleEditReport(report)} className="p-2 text-teal-600 hover:bg-teal-50 rounded-lg transition-colors" title="Rediger rapport" data-id="element-1753">
                        <EditIcon className="w-5 h-5" data-id="element-1754" />
                      </button>
                      <button onClick={() => handleDeleteReport(report.id!)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Slet rapport" data-id="element-1755">
                        <Trash2Icon className="w-5 h-5" data-id="element-1756" />
                      </button>
                    </>}
                </div>
              </motion.div>)}
          </div> : <div className="text-center py-12" data-id="element-1757">
            <FileTextIcon className="w-16 h-16 text-stone-300 mx-auto mb-4" data-id="element-1758" />
            {searchQuery ? <>
                <p className="text-stone-600 font-medium mb-1" data-id="element-1759">
                  Ingen rapporter fundet
                </p>
                <p className="text-sm text-stone-500" data-id="element-1760">
                  Prøv at søge efter noget andet
                </p>
              </> : <>
                <p className="text-stone-600 font-medium mb-1" data-id="element-1761">
                  Ingen rapporter endnu
                </p>
                <p className="text-sm text-stone-500 mb-4" data-id="element-1762">
                  {isReadOnly ? 'Der er ingen rapporter i systemet' : 'Kom i gang ved at oprette din første rapport'}
                </p>
                {!isReadOnly && <Button onClick={() => {
            setEditingReport(null);
            setIsReportModalOpen(true);
          }} className="inline-flex items-center gap-2" data-id="element-1763">
                    <PlusIcon className="w-5 h-5" data-id="element-1764" />
                    Tilføj Rapport
                  </Button>}
              </>}
          </div>}
      </motion.div>

      {/* Add/Edit Report Modal */}
      {!isReadOnly && <AddReportModal isOpen={isReportModalOpen} onClose={() => {
      setIsReportModalOpen(false);
      setEditingReport(null);
    }} onAdd={handleAddOrUpdateReport} customers={customers} tasks={tasks} editingReport={editingReport} data-id="element-1765" />}
    </div>;
}